import React from 'react';
import MuiErrorIcon from '@material-ui/icons/Error';
import MuiCheckCircleIcon from '@material-ui/icons/CheckCircle';
import MuiGetAppIcon from '@material-ui/icons/GetApp';

// import PropTypes, { SvgIconTypeMap } from '@material-ui/core';

export type ThemeColors =
  | 'inherit'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'info'
  | 'warning'
  | 'error'
  | 'action'
  | 'disabled';
type IconColors = 'inherit' | 'primary' | 'secondary' | 'action' | 'disabled' | 'error';
type IconSize = 'inherit' | 'default' | 'small' | 'large';
type IconProps = {
  color?: IconColors;
  fontSize?: IconSize;
  htmlColor?: string;
  className?: string;
  style?: React.CSSProperties;
  cssColor?: string;
};

export const ErrorIcon = ({
  color = 'inherit',
  fontSize = 'inherit',
  className = '',
  htmlColor,
  cssColor,
}: IconProps = {}) => {
  return (
    <MuiErrorIcon
      className={className}
      color={color}
      fontSize={fontSize}
      htmlColor={htmlColor}
      style={{ fill: cssColor }}
    />
  );
};

export const CheckCircleIcon = ({
  color = 'inherit',
  fontSize = 'inherit',
  className = '',
  htmlColor,
  style,
  cssColor,
}: IconProps = {}) => {
  return (
    <MuiCheckCircleIcon
      className={className}
      color={color}
      fontSize={fontSize}
      htmlColor={htmlColor}
      style={{ ...style, fill: cssColor }}
    />
  );
};

export const GetAppIcon = ({
  color = 'inherit',
  fontSize = 'inherit',
  className = '',
  htmlColor,
  style,
  cssColor,
}: IconProps = {}) => {
  return (
    <MuiGetAppIcon
      className={className}
      color={color}
      fontSize={fontSize}
      htmlColor={htmlColor}
      style={{ ...style, fill: cssColor }}
    />
  );
};

/**
 *
 * @param {string} [cssColor] Can be any CSS color expression: 'var(--text-color-disabled)' or `red`
 * @returns
 */
export const CodeOutputIcon = ({
  cssColor,
  fontSize = 'large',
}: {
  cssColor: string;
  fontSize: 'large' | 'medium' | 'small';
}) => {
  const className =
    fontSize === 'large'
      ? `MuiSvgIcon-fontSizeLarge`
      : fontSize === 'medium'
      ? `MuiSvgIcon-fontSizeMedium`
      : fontSize === 'small'
      ? `MuiSvgIcon-fontSizeSmall`
      : '';
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 29 29"
      className={`MuiSvgIcon-root ${className}`}
    >
      <path
        style={{ fill: cssColor }}
        d="M3 0v29h22.3V7.35l-.3-.34L18.3.3 17.95 0H3zm2.23 2.23h11.15v6.7h6.7v17.84H5.23V2.23zm13.39 1.6l2.85 2.86h-2.85V3.83zm-4.47 7.32l-2.23 13.39h2.23l2.23-13.39h-2.23zM9.94 13.8l-2.8 3.35-.58.7.59.7 2.79 3.34 1.74-1.4-2.2-2.64 2.2-2.65-1.74-1.4zm8.43 0l-1.74 1.4 2.2 2.65-2.2 2.65 1.74 1.39 2.79-3.35.6-.7-.6-.7-2.79-3.34z"
      />
    </svg>
  );
};

export const MongoDbIcon = (...props) => (
  <svg xmlns="http://www.w3.org/2000/svg" role="img" viewBox="0 0 37 81">
    <path
      fill="#599636"
      d="M17.72.22l2.13 4a11.98 11.98 0 001.61 2.01 56.02 56.02 0 015.01 5.67c3.62 4.75 6.06 10 7.8 15.73a38.65 38.65 0 011.66 10.67 37.67 37.67 0 01-11 27.8A31.74 31.74 0 0121 69.45c-.74 0-1.09-.56-1.4-1.09a8.96 8.96 0 01-1.08-3.13c-.26-1.31-.44-2.62-.35-3.97v-.6C18.1 60.5 17.46.5 17.7.21z"
    />
    <path
      fill="#6cac48"
      d="M17.72.09c-.1-.18-.18-.05-.27.04.05.87-.26 1.65-.74 2.4-.5.74-1.22 1.3-1.91 1.92a41.76 41.76 0 00-9.37 11.94A47.95 47.95 0 00.03 35.9C-.19 38.4.82 47.15 1.6 49.67 3.73 56.38 7.57 62 12.53 66.88a43.78 43.78 0 003.88 3.31c.4 0 .43-.35.5-.6a11.96 11.96 0 00.4-1.7l.87-6.54L17.72.08z"
    />
    <path
      fill="#c2bfbf"
      d="M19.85 72.1c.09-1 .57-1.82 1.1-2.65-.5-.22-.92-.65-1.23-1.13a8.12 8.12 0 01-.65-1.44c-.6-1.83-.74-3.75-.91-5.62v-1.13c-.22.18-.27 1.66-.27 1.88a43.3 43.3 0 01-.78 5.88c-.13.78-.22 1.56-.7 2.26 0 .09 0 .18.04.3a26 26 0 011.13 7.07v.87c0 1.05-.04.83.83 1.18.35.13.74.17 1.09.43.26 0 .3-.22.3-.4l-.13-1.43v-4c-.04-.7.09-1.4.18-2.06z"
    />
  </svg>
);

export const PostgresIcon = (...props) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" role="img" viewBox="0 0 24 24">
      <path
        fill="#336791"
        d="M17.13 0a10.13 10.13 0 00-2.76.4l-.06.02A10.92 10.92 0 0012.6.26a5.71 5.71 0 00-3 .74A11.6 11.6 0 005.35.34a5.15 5.15 0 00-3.55 1.48C.83 2.87.31 4.48.41 6.68c.04.6.2 1.6.5 2.88s.68 2.78 1.19 4.15c.5 1.37 1.05 2.6 1.91 3.44.43.42 1.03.77 1.72.74a2.2 2.2 0 001.32-.55c.18.24.38.35.56.45.23.13.45.21.68.27.42.1 1.12.24 1.95.1.28-.05.58-.14.88-.27 0 .33.02.65.03.98.05 1.03.07 1.99.38 2.83.05.14.19.84.73 1.46.54.63 1.6 1.02 2.8.76a4.06 4.06 0 002.65-1.53c.71-1.01 1.03-2.46 1.1-4.81l.05-.34.17.02h.02c.9.04 1.89-.1 2.71-.47a3.7 3.7 0 001.68-1.29c.1-.15.21-.33.24-.64s-.15-.8-.44-1.02c-.6-.46-.97-.28-1.37-.2a6.27 6.27 0 01-1.2.15 23 23 0 002.45-5.85c.28-1.08.44-2.08.45-2.95.01-.87-.06-1.64-.58-2.3A7.17 7.17 0 0017.3 0h-.16zm-.05.64a6.5 6.5 0 015.36 2.42c.35.44.45 1.09.44 1.89-.01.8-.16 1.74-.43 2.79a22.42 22.42 0 01-2.9 6.48.76.76 0 00.16.09c.3.12.95.22 2.27-.05.33-.07.58-.11.83.08a.52.52 0 01.18.42.7.7 0 01-.13.34c-.25.38-.76.74-1.4 1.04a5.52 5.52 0 01-3.1.3h-.02a84.16 84.16 0 01-.53 4.1 3.36 3.36 0 01-.8 1.84c-.44.46-1.07.74-1.9.92-1.04.22-1.8-.02-2.29-.43a3.12 3.12 0 01-.84-1.29c-.1-.23-.14-.52-.19-.92-.04-.4-.08-.89-.1-1.44a51.43 51.43 0 01-.03-2.52 3.06 3.06 0 01-1.55.76c-.7.12-1.3 0-1.67-.09a2.28 2.28 0 01-.52-.2.96.96 0 01-.44-.4.56.56 0 01-.06-.38.61.61 0 01.22-.33c.2-.16.46-.25.85-.33.72-.15.97-.25 1.13-.37.13-.1.27-.32.53-.62a1.16 1.16 0 010-.05 2.96 2.96 0 01-1.33-.35c-.15.15-.92.96-1.85 2.09-.4.47-.83.74-1.28.76-.46.02-.88-.21-1.23-.55a8.86 8.86 0 01-1.75-3.2 33.3 33.3 0 01-1.64-6.82c-.1-2.09.38-3.49 1.21-4.37A4.5 4.5 0 015.38.96c2-.11 3.91.59 4.3.74.74-.5 1.7-.82 2.89-.8a7.39 7.39 0 011.68.22l.02-.01a6.85 6.85 0 01.74-.22 9.63 9.63 0 012.07-.25zm.15.67h-.14a8.74 8.74 0 00-1.7.2 7.5 7.5 0 012.84 2.24 8.44 8.44 0 011.14 1.92 4.6 4.6 0 01.26.9l-.01.14v.01c.02.88-.2 1.47-.22 2.31-.02.6.14 1.32.17 2.1.04.73-.05 1.53-.52 2.31l.11.15a21.85 21.85 0 002.64-6.02c.26-1 .4-1.92.4-2.64.02-.72-.12-1.24-.29-1.46a5.74 5.74 0 00-4.68-2.16zm-4.79.26c-1.18 0-2.03.36-2.67.9a4.95 4.95 0 00-1.4 2.08 8.84 8.84 0 00-.51 2.41h.01c.36-.2.83-.4 1.33-.52.5-.12 1.04-.15 1.53.04s.9.63 1.04 1.31c.7 3.26-.22 4.47-.56 5.38a9.61 9.61 0 00-.33 1.02l.13-.03c.24-.02.43.06.54.1.34.15.58.45.7.79.04.09.06.18.08.28a.34.34 0 01.02.13 55.14 55.14 0 00.01 3.74c.02.53.06 1 .1 1.38a2.54 2.54 0 00.8 1.78c.33.28.82.47 1.7.28.77-.16 1.25-.4 1.56-.72.32-.33.5-.79.63-1.49.18-1.05.55-4.1.59-4.67-.02-.43.04-.76.18-1.01.14-.26.36-.42.55-.5.1-.05.19-.08.26-.1a5.96 5.96 0 00-.24-.33 4.46 4.46 0 01-.67-1.1 8.3 8.3 0 00-.26-.48l-.47-.87a10.9 10.9 0 01-.94-2.3c-.2-.8-.22-1.61.28-2.2.45-.51 1.24-.73 2.43-.6l-.12-.34a7.82 7.82 0 00-1.04-1.76 6.47 6.47 0 00-5.14-2.6h-.12zm-6.64.05h-.38A3.82 3.82 0 002.77 2.7c-.68.73-1.13 1.91-1.03 3.88a32.9 32.9 0 001.6 6.63 8.7 8.7 0 001.59 2.94c.27.27.51.37.73.36.21 0 .47-.13.8-.51a43.24 43.24 0 011.8-2.05A3.5 3.5 0 017.1 10.8c.1-.74.11-1.43.1-1.98-.01-.53-.05-.88-.05-1.1a.34.34 0 010-.02v-.02a9.9 9.9 0 01.6-3.37c.27-.75.69-1.5 1.31-2.11-.61-.2-1.7-.51-2.88-.57a7.6 7.6 0 00-.38-.01zM18.2 6.9c-.68 0-1.06.18-1.26.41-.28.33-.31.9-.14 1.6.18.7.54 1.49.88 2.14l.47.86.3.56c.05.13.1.25.17.36.26-.56.31-1.1.28-1.67-.03-.7-.2-1.42-.17-2.15.03-.85.2-1.4.2-2.06a5.8 5.8 0 00-.73-.05zm-8.23.12a2.82 2.82 0 00-.62.07 4.67 4.67 0 00-1.15.45 2.42 2.42 0 00-.35.23l-.02.02c0 .14.03.5.04 1.02.01.57 0 1.3-.11 2.08-.24 1.71 1 3.13 2.46 3.13.09-.35.23-.7.36-1.08.41-1.1 1.21-1.9.54-5-.11-.52-.33-.73-.63-.84A1.47 1.47 0 009.97 7zm7.91.2h.05c.07 0 .13 0 .18.02.06.01.1.03.14.06a.16.16 0 01.08.1v.01a.24.24 0 01-.04.14.67.67 0 01-.11.15.68.68 0 01-.39.21.59.59 0 01-.4-.1.6.6 0 01-.14-.12.26.26 0 01-.06-.13.17.17 0 01.04-.12.38.38 0 01.12-.1 1.12 1.12 0 01.53-.12zm-7.82.17l.17.01c.15.02.29.06.4.12a.45.45 0 01.12.1.22.22 0 01.06.18.3.3 0 01-.08.15.65.65 0 01-.14.13.64.64 0 01-.45.12.73.73 0 01-.42-.23.71.71 0 01-.12-.17.28.28 0 01-.04-.17c.02-.11.1-.17.2-.2a.87.87 0 01.3-.04zm9.06 7.34l-.37.12a.45.45 0 00-.24.21c-.06.12-.12.32-.1.67a.51.51 0 00.15.07c.17.05.45.09.77.08.64 0 1.43-.15 1.84-.35a3.95 3.95 0 00.95-.61c-1.4.28-2.18.2-2.67 0a1.31 1.31 0 01-.33-.2zm-8.03.1h-.02c-.05 0-.13.02-.28.18-.35.4-.47.64-.76.87-.28.22-.66.35-1.4.5-.24.05-.37.1-.46.14l.07.06c.1.06.25.1.36.14.32.08.85.17 1.4.08.54-.1 1.11-.36 1.6-1.04.09-.12.1-.3.03-.48a.74.74 0 00-.34-.4.65.65 0 00-.2-.05z"
      />
    </svg>
  );
};

export const KnexIcon = (...props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      fillRule="evenodd"
      stroke="#000"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 81 81"
    >
      <path
        fill="#e16426"
        d="M79.6 44.4l-9.9-4.8 9.3-4.3c.8-.4 1.2-1.3.7-2.1l-1-1.1c-.6-.7-1.3-1.2-2.1-.9l-7 2.6a.8.8 0 01-.4 0l-2.7-.7-.6.1-2.8 2.8c-.7-3.7-2.2-7.1-4.3-10H63a.5.5 0 00.4-.3l1.5-2.7.3-.3 6.9-3c.4-.1.7-1 .6-1.6l-.2-2c-.2-.6-1-1.1-1.4-1l-10.4 3.6L64.3 9c.2-.7-.1-1.6-1-1.9h-1.5c-1-.1-1.8 0-2.1.8l-3.1 6.8-.3.3-2.4 1.4c-.2 0-.3.3-.3.5l-.2 4c-2.9-2.1-6.3-3.5-10-4l3-3a.5.5 0 000-.6l-.8-3a.7.7 0 010-.4l2.8-7c.2-.3-.3-1.1-.7-1.5L46 .1c-.6-.3-1.5 0-1.7.3l-4.8 9.9L35.3 1C34.9.2 34-.2 33.2.3l-1.1 1c-.7.6-1.2 1.3-.9 2.1l2.6 7v.4l-.7 2.7.1.6 2.8 2.8a23.3 23.3 0 00-10 4.3V17a.5.5 0 00-.3-.4L23 15.1a.7.7 0 01-.3-.3l-3-6.9c-.1-.4-1-.7-1.6-.6l-2 .2c-.6.2-1.1 1-1 1.4l3.6 10.4-9.6-3.6c-.8-.2-1.7.1-2 1v1.5c-.1 1 0 1.8.8 2.1l6.8 3.1.3.3 1.4 2.5.5.3h4a23.3 23.3 0 00-4 10.1l-3-3a.5.5 0 00-.6 0l-3 .8H10L3 31.6c-.4-.2-1.2.3-1.6.7L.1 34c-.3.6 0 1.5.3 1.7l9.9 4.8L1 44.7c-.8.4-1.2 1.3-.7 2.1l1 1.1c.6.7 1.3 1.2 2.1.9l7-2.6a.8.8 0 01.4 0l2.7.7.6-.1 2.8-2.7a23.3 23.3 0 004.3 10H17a.5.5 0 00-.4.2L15.1 57a.7.7 0 01-.3.3l-6.9 3c-.4.1-.7 1-.6 1.6l.2 2c.2.6 1 1.1 1.4 1l10.4-3.6-3.6 9.7c-.2.7.1 1.6 1 1.9h1.5c1 .1 1.8 0 2.1-.8l3.1-6.8a.8.8 0 01.3-.3l2.5-1.4.3-.5v-4a23.3 23.3 0 0010.1 4l-3 3a.5.5 0 000 .6l.8 3a.7.7 0 010 .4l-2.8 7c-.2.3.3 1.1.7 1.5l1.6 1.3c.6.3 1.5 0 1.7-.3l4.8-9.9 4.3 9.3c.4.8 1.3 1.2 2.1.7l1.1-1c.7-.6 1.2-1.3.9-2.1l-2.6-7v-.4l.7-2.7-.1-.6-2.8-2.8c3.7-.7 7.1-2.2 10-4.3V63a.5.5 0 00.3.4l2.7 1.5.3.3 3 6.9c.1.4 1 .7 1.6.6l2-.2c.6-.2 1.1-1 1-1.4l-3.6-10.4 9.7 3.6c.7.2 1.6-.1 1.9-1v-1.5c.1-1 0-1.8-.8-2.1l-6.8-3a.8.8 0 01-.3-.4l-1.4-2.4a.6.6 0 00-.5-.3l-4-.1a23.3 23.3 0 004-10.1l3 3a.5.5 0 00.6 0l3-.8h.4l6.9 2.8c.4.2 1.2-.3 1.6-.7l1.3-1.6c.3-.6 0-1.5-.3-1.7h0zm-22.8-7h-5a12 12 0 00-2-4.5l3.5-3.5a17 17 0 013.5 8h0zm-10.3-7.6a12 12 0 00-4.6-1.7v-5c3 .4 5.8 1.5 8.1 3.2l-3.5 3.5zM46 40a5.9 5.9 0 01-5.9 5.9 5.9 5.9 0 01-5.9-5.9 5.9 5.9 0 015.9-5.9 5.9 5.9 0 015.9 5.9h0zm-8.5-16.8v5a12 12 0 00-4.5 2l-3.5-3.5a17 17 0 018-3.5h0zm-7.6 10.3a12 12 0 00-1.7 4.6h-5a16.9 16.9 0 013.2-8.1l3.5 3.5zm-6.6 9.1h5a12 12 0 002 4.5l-3.5 3.5a17 17 0 01-3.5-8h0zm10.3 7.6a12 12 0 004.6 1.7v5a16.9 16.9 0 01-8.1-3.2l3.5-3.5zm9.1 6.6v-5a12 12 0 004.5-2l3.5 3.5a17 17 0 01-8 3.5h0zm7.6-10.3a12 12 0 001.7-4.6h5a16.9 16.9 0 01-3.2 8.1l-3.5-3.5z"
      />
    </svg>
  );
};

export const TypeScriptIcon = (...props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    fill="#fff"
    fillRule="evenodd"
    stroke="#000"
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 85 85"
  >
    <path
      fill="#007acc"
      stroke="none"
      d="M0 40v40h80V0H0v40zm64.5-3.2a10 10 0 015 2.9c.7.8 1.8 2.2 1.9 2.6L65.8 46s-.4-.3-.7-.8c-1-1.5-2.1-2.1-3.8-2.2-2.4-.2-4 1-4 3.2 0 .6.2 1 .4 1.5.5 1 1.5 1.7 4.6 3.1 5.8 2.5 8.2 4 9.8 6.4 1.7 2.6 2 6.7.9 9.7-1.3 3.4-4.4 5.6-8.9 6.4-1.3.2-4.6.2-6 0-3.3-.7-6.3-2.2-8.2-4.3-.7-.8-2.2-3-2-3.1l.6-.5 3-1.7 2.3-1.3.5.7c.7 1 2.1 2.4 3 2.9 2.6 1.3 6 1.2 7.8-.4a3 3 0 001-2.4 3 3 0 00-.5-2c-.7-1-2-1.7-5.5-3.2-4.2-1.8-6-3-7.6-4.7-1-1-1.8-2.6-2.2-4a16 16 0 01-.1-5c.8-4 3.9-6.8 8.2-7.7 1.4-.2 4.7-.1 6 .2zm-18.8 3.4v3.2H35.3V73h-7.4V43.4H17.5v-3.2l.1-3.3h28v3.3z"
    />
  </svg>
);

export const GoLangIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    data-style="-ms-transform:rotate(360deg);-webkit-transform:rotate(360deg)"
    aria-hidden="true"
    transform="rotate(360)"
    viewBox="0 0 24 24"
  >
    <g fill="none">
      <path
        fill="#8CC5E7"
        d="M21.47 3.2c.71 1.48-1.46 1.64-1.35 2.3.2 1.18.07 2.94-.09 4.67-.27 2.94 1.93 9.98-2.65 12.8-.86.54-2.9.84-4.93.88h-.02c-2.02-.05-4.34-.34-5.2-.88-4.57-2.82-2.38-9.86-2.64-12.8-.16-1.73-.3-3.49-.09-4.67.11-.66-2.06-.81-1.34-2.3.6-1.26 2.12-.14 2.5-.54C7.6.65 9.95.1 11.8.06h1.02c1.85.1 4.2.6 6.15 2.6.38.4 1.88-.72 2.5.55zm-10.04 7.7c-.08.04-.22.95.2 1 .3.03 1.08.12 1.27 0 .36-.23.32-.79.12-.93-.35-.22-1.5-.1-1.59-.06zM9.08 4.1c-.95-.1-2.56.78-2.8 2.68-.26 1.96 2.06 3.9 4.28 1.96 1.2-1.05 1.62-4.27-1.48-4.63zm6.45 0c-3.1.37-2.68 3.6-1.48 4.64 2.22 1.95 4.54 0 4.28-1.96-.23-1.9-1.84-2.78-2.8-2.67z"
      />
      <path
        fill="#B8937F"
        d="M12.31 8.99c.59-.09 1.99.58 1.9 1.37-.12.92-3.61 1.04-3.8-.06-.1-.66.42-1.09 1.9-1.31zm7.89 7.38c-.29 0-.47-.39-.47-.62 0-.41.05-.9.36-1.12.63-.43 1.13 1.75.1 1.74zm-15.78 0c-1.02.01-.52-2.17.1-1.74.32.21.37.7.37 1.12 0 .23-.18.61-.47.62zm13.84 6.72c.19.27.2.46-.1.6-1.13.51-2.14-.18-1.76-.38.8-.4 1.3-1.04 1.86-.22zm-11.9.1c.55-.83 1.05-.2 1.86.22.38.2-.63.89-1.76.37-.3-.14-.3-.33-.1-.6z"
      />
      <path
        fill="#000"
        d="M19.73 3.43c.05-.2.53-.29.8.04.33.4-.47.95-.5.73-.1-.6-.35-.58-.3-.77zm-14.85 0c.06.2-.2.16-.29.77-.03.22-.83-.33-.5-.73.25-.33.74-.24.8-.04zm10.86 4.51a1.06 1.06 0 110-2.11 1.06 1.06 0 010 2.11zm-.28-1.18a.33.33 0 100-.66.33.33 0 000 .66zm-4.1 2.67c.1-.42.5-.56.81-.57.81-.02 1.12.42 1.14.76.06.58-2.2.72-1.95-.19zM8.88 7.94a1.06 1.06 0 110-2.11 1.06 1.06 0 010 2.11zm.27-1.18a.33.33 0 100-.66.33.33 0 000 .66z"
      />
    </g>
  </svg>
);

export const GitHubIcon = (...props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <path d="M12 .3a12 12 0 00-3.8 23.4c.6.1.8-.3.8-.6v-2c-3.3.7-4-1.6-4-1.6-.6-1.4-1.4-1.8-1.4-1.8-1-.7.1-.7.1-.7 1.2 0 1.9 1.2 1.9 1.2 1 1.8 2.8 1.3 3.5 1 0-.8.4-1.3.7-1.6-2.7-.3-5.5-1.3-5.5-6 0-1.2.5-2.3 1.3-3.1-.2-.4-.6-1.6 0-3.2 0 0 1-.3 3.4 1.2a11.5 11.5 0 016 0c2.3-1.5 3.3-1.2 3.3-1.2.6 1.6.2 2.8 0 3.2a4 4 0 011.3 3.2c0 4.6-2.8 5.6-5.5 5.9.5.4.9 1 .9 2.2v3.3c0 .3.1.7.8.6A12 12 0 0012 .3" />
  </svg>
);

// credit: https://iconify.design/icon-sets/?query=data
export const DatabaseEditIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    style={{ transform: 'rotate(360deg)', width: '1.5rem', height: '1.5rem' }}
    preserveAspectRatio="xMidYMid meet"
    viewBox="0 0 24 24"
  >
    <path
      d="M4 14v3c0 2 3.05 3.72 7 4v-2.89l.13-.11C7.12 17.76 4 16.06 4 14m8-1c-4.42 0-8-1.79-8-4v3c0 2.21 3.58 4 8 4h1.16L17 12.12c-1.6.6-3.29.88-5 .88m0-10C7.58 3 4 4.79 4 7s3.58 4 8 4s8-1.79 8-4s-3.58-4-8-4m9 8.13c-.15 0-.29.06-.39.17l-1 1l2.05 2l1-1a.54.54 0 0 0 0-.77l-1.24-1.23a.517.517 0 0 0-.38-.17m-2 1.75L13 18.94V21h2.06l6.06-6.07l-2.08-2.05z"
      fill="#666666"
    />
  </svg>
);

export const ZodIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fillRule="evenodd"
    strokeMiterlimit="5"
    clipRule="evenodd"
    viewBox="0 0 1080 1080"
  >
    <path fill="#274d82" d="M334.61 662.1h406.94v64.29H334.61z" />
    <path
      fill="#274d82"
      d="M378.84 715.38h315.24v64.28H378.84zM603.16 645.42h168.69v18.1H603.16zM271.6 590.55l602.55-347.89 60.59 104.93-602.56 347.89zM125.14 430.54 511.29 207.6l12.45 21.56L137.58 452.1z"
    />
    <path fill="#274d82" d="m112.58 415.92 386.15-222.95 12.45 21.55-386.16 222.95z" />
    <path
      fill="#274d82"
      d="m100.13 404.49 386.15-222.95 12.45 21.55-386.16 222.95zM137.59 443.36 545.96 207.6l9.8 16.97-408.37 235.78z"
    />
    <path fill="#274d82" d="m147.39 457 424.05-244.83 9.8 16.97-424.05 244.83z" />
    <path
      fill="#274d82"
      d="M154.54 469.4 608 207.6l12.44 21.55L167 490.95zM717.4 313.89l136.25-78.67L893.93 305l-136.25 78.66z"
    />
    <path
      fill="#3068b7"
      d="M842.64 179.14H258.38L44.9 391.51l484.64 583.6 38.66-43.39 479.72-538.22-205.3-214.36Zm-21.35 50 158.57 165.58L530.9 898.45 112.58 394.72 279 229.14H821.3Z"
    />
  </svg>
);
